import React, { useState } from "react";
import {
  Typography,
  TextField,
  Button,
  Stack,
  Box,
  MenuItem,
  Select,
  FormControl,
  FormHelperText,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AuthCard from "../../components/layout/AuthCard";
import { useLocation } from "react-router-dom";
import { useGetRegistrationDataQuery } from "../../redux/api/authApi";
import ReqMark from "./ReqMark";

export default function Step5fileSubmit({
  nextStep,
  prevStep,
  defaultValues = {},
}) {
  const [investmentExperience, setInvestmentExperience] = useState(
    defaultValues.investmentExperience || ""
  );
  const [riskTolerance, setRiskTolerance] = useState(
    defaultValues.riskTolerance || ""
  );
  const [primaryInvestmentGoal, setPrimaryInvestmentGoal] = useState(
    defaultValues.primaryInvestmentGoal || ""
  );
  const [annualIncome, setAnnualIncome] = useState(
    defaultValues.annualIncome || ""
  );
  const [netWorth, setNetWorth] = useState(defaultValues.netWorth || "");
  const [errors, setErrors] = useState({});

  const {
    data: registrationData,
    isLoading,
    isError,
  } = useGetRegistrationDataQuery();

  console.warn("registrationData -> ", registrationData?.data);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const role = queryParams.get("role");

  const handleNextClick = () => {
    const newErrors = {};
    if (!investmentExperience) newErrors.investmentExperience = "Please select investment experience.";
    if (!riskTolerance) newErrors.riskTolerance = "Please select risk tolerance.";
    if (!primaryInvestmentGoal.trim()) newErrors.primaryInvestmentGoal = "Primary investment goal is required.";
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    if (typeof nextStep === "function") {
      nextStep({ investmentExperience, riskTolerance, primaryInvestmentGoal, annualIncome, netWorth });
    }
  };

  if (isLoading) return <div>Loading...</div>;
  if (isError) return <div>Error loading data</div>;

  return (
    <AuthCard>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Typography variant="h6" fontWeight={700}>
          Create Account
        </Typography>
        <Button
          variant="outlined"
          size="small"
          sx={{
            color: "#38bdf8",
            borderColor: "rgba(56,189,248,0.4)",
            borderRadius: "999px",
            textTransform: "none",
            px: 2,
            height: 28,
            fontSize: 12,
            "&:hover": { background: "rgba(56,189,248,0.1)" },
          }}
        >
          {role === "investor"
            ? "Investor"
            : role === "assetOwner"
            ? "Asset Owner"
            : "Admin"}
        </Button>
      </Stack>
      <Typography color="text.secondary" sx={{ mb: 0.5 }}>
        Step 4 of 5 - Financial Profile
      </Typography>
      <Typography variant="caption" sx={{ display: "block", color: "#f87171", mb: 2 }}>
        * Required fields must be completed. Fields labeled Optional may be left blank.
      </Typography>

      {/* Segmented Progress Bar */}
      <Stack direction="row" spacing={1} sx={{ mb: 4 }}>
        {[1, 2, 3, 4, 5].map((bar, index) => (
          <Box
            key={bar}
            sx={{
              flex: 1,
              height: 3,
              borderRadius: 1,
              backgroundColor: index < 4 ? "#0ea5e9" : "rgba(255,255,255,0.1)",
            }}
          />
        ))}
      </Stack>

      <Stack spacing={2}>
        <Box>
          <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 700, color: "#cbd5e1" }}>
            Investment Experience
            <ReqMark />
          </Typography>
          <FormControl fullWidth error={!!errors.investmentExperience}>
            <Select
              displayEmpty
              value={investmentExperience}
              onChange={(e) => { setInvestmentExperience(e.target.value); setErrors((p) => ({ ...p, investmentExperience: "" })); }}
              sx={{ backgroundColor: "rgba(255,255,255,0.05)", borderRadius: "10px" }}
            >
              <MenuItem value="" disabled><em>Select experience</em></MenuItem>
              {registrationData?.data?.investmentExperienceOptions?.map((option) => (
                <MenuItem key={option.key} value={option.key}>{option.label}</MenuItem>
              ))}
            </Select>
            {errors.investmentExperience && <FormHelperText>{errors.investmentExperience}</FormHelperText>}
          </FormControl>
        </Box>

        <Box>
          <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 700, color: "#cbd5e1" }}>
            Risk Tolerance
            <ReqMark />
          </Typography>
          <FormControl fullWidth error={!!errors.riskTolerance}>
            <Select
              displayEmpty
              value={riskTolerance}
              onChange={(e) => { setRiskTolerance(e.target.value); setErrors((p) => ({ ...p, riskTolerance: "" })); }}
              sx={{ backgroundColor: "rgba(255,255,255,0.05)", borderRadius: "10px" }}
            >
              <MenuItem value="" disabled><em>Select risk tolerance</em></MenuItem>
              {registrationData?.data?.riskToleranceOptions?.map((option) => (
                <MenuItem key={option.key} value={option.key}>{option.label}</MenuItem>
              ))}
            </Select>
            {errors.riskTolerance && <FormHelperText>{errors.riskTolerance}</FormHelperText>}
          </FormControl>
        </Box>

        <Box>
          <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 700, color: "#cbd5e1" }}>
            Primary Investment Goals
            <ReqMark />
          </Typography>
          <TextField
            placeholder="Enter your primary investment goal"
            fullWidth
            value={primaryInvestmentGoal}
            onChange={(e) => { setPrimaryInvestmentGoal(e.target.value); setErrors((p) => ({ ...p, primaryInvestmentGoal: "" })); }}
            error={!!errors.primaryInvestmentGoal}
            helperText={errors.primaryInvestmentGoal}
            sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "rgba(255,255,255,0.05)", borderRadius: "10px" } }}
          />
        </Box>

        <Box>
          <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 700, color: "#cbd5e1" }}>
            Annual Income (Optional)
          </Typography>
          <FormControl fullWidth>
            <Select
              displayEmpty
              value={annualIncome}
              onChange={(e) => setAnnualIncome(e.target.value)}
              sx={{ backgroundColor: "rgba(255,255,255,0.05)", borderRadius: "10px" }}
            >
              <MenuItem value=""><em>Select annual income range</em></MenuItem>
              {Object.entries(registrationData?.data?.annualIncomeOptions || {}).map(([key, value]) => (
                <MenuItem key={key} value={value}>{value}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box>
          <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 700, color: "#cbd5e1" }}>
            Net Worth (Optional)
          </Typography>
          <FormControl fullWidth>
            <Select
              displayEmpty
              value={netWorth}
              onChange={(e) => setNetWorth(e.target.value)}
              sx={{ backgroundColor: "rgba(255,255,255,0.05)", borderRadius: "10px" }}
            >
              <MenuItem value=""><em>Select net worth range</em></MenuItem>
              {Object.entries(registrationData?.data?.netWorthOptions || {}).map(([key, value]) => (
                <MenuItem key={key} value={value}>{value}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Stack>

      <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          fullWidth
          sx={{
            background: "linear-gradient(90deg,#0284c7,#0ea5e9)",
            borderRadius: "12px",
            textTransform: "none",
          }}
          onClick={prevStep}
        >
          Back
        </Button>
        <Button
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          fullWidth
          sx={{
            background: "linear-gradient(90deg,#0284c7,#0ea5e9)",
            borderRadius: "12px",
            textTransform: "none",
          }}
          onClick={handleNextClick}
        >
          Continue
        </Button>
      </Stack>
    </AuthCard>
  );
}
